import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, orderBy, onSnapshot, doc, deleteDoc, getDocs } from "firebase/firestore";
import { Search, Plus, Filter, ChevronRight, Clock, CheckCircle, ArrowLeft, MoreVertical, Edit2, Trash2, Calendar, Hash, List, Table, X } from 'lucide-react';
import Spinner from './Spinner.js';
import useClickOutside from './hooks/useClickOutside.js';

const InventoryTransferList = ({ db, user }) => {
  const navigate = useNavigate();
  const [transfers, setTransfers] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [viewMode, setViewMode] = useState('slips');
  
  // Filter States
  const [skuChips, setSkuChips] = useState([]);
  const [skuInput, setSkuInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useClickOutside(() => setShowSuggestions(false));

  const [fromWh, setFromWh] = useState('All');
  const [toWh, setToWh] = useState('All');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [txnTypeFilter, setTxnTypeFilter] = useState('All');
  const [reasonCodeFilter, setReasonCodeFilter] = useState('All');

  useEffect(() => {
    // 1. Fetch Transfers
    const q = query(collection(db, "inventory_transfers"), orderBy("slipDate", "desc"));
    const unsubTransfers = onSnapshot(q, (snapshot) => {
      setTransfers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    });

    // 2. Fetch Products for Search
    const fetchProducts = async () => {
      const snap = await getDocs(collection(db, "products"));
      setAllProducts(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };
    fetchProducts();

    return () => unsubTransfers();
  }, [db]);

  // Search Results for Dropdown
  const searchedProducts = useMemo(() => {
    if (!skuInput) return [];
    return allProducts.filter(p => 
      p.sku?.toLowerCase().includes(skuInput.toLowerCase()) || 
      p.name?.toLowerCase().includes(skuInput.toLowerCase())
    ).slice(0, 10);
  }, [skuInput, allProducts]);

  const addSkuChip = (sku) => {
    const cleanSku = sku.trim().toLowerCase();
    if (cleanSku && !skuChips.includes(cleanSku)) {
      setSkuChips([...skuChips, cleanSku]);
    }
    setSkuInput('');
    setShowSuggestions(false);
  };

  const handleSkuKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addSkuChip(skuInput);
    } else if (e.key === 'Backspace' && !skuInput && skuChips.length > 0) {
      setSkuChips(skuChips.slice(0, -1));
    }
  };

  const removeSkuChip = (chip) => {
    setSkuChips(skuChips.filter(c => c !== chip));
  };

  // 1. FILTER DOCUMENTS
  const filteredDocs = useMemo(() => {
    return transfers.filter(t => {
      // SKU Search
      const matchesSku = skuChips.length === 0 || t.components?.some(c => 
        skuChips.some(ts => c.sku?.toLowerCase().includes(ts))
      );
      
      const matchesFrom = fromWh === 'All' || t.fromWarehouse === fromWh;
      const matchesTo = toWh === 'All' || t.toWarehouse === toWh;
      const matchesTxn = txnTypeFilter === 'All' || t.txnType === txnTypeFilter;
      const matchesReason = reasonCodeFilter === 'All' || t.reasonCode === reasonCodeFilter;
      
      const tDate = new Date(t.slipDate);
      const matchesStart = !startDate || tDate >= new Date(startDate);
      const matchesEnd = !endDate || tDate <= new Date(endDate);

      return matchesSku && matchesFrom && matchesTo && matchesTxn && matchesReason && matchesStart && matchesEnd;
      });
  }, [transfers, skuChips, fromWh, toWh, txnTypeFilter, reasonCodeFilter, startDate, endDate]);

  // 2. FLATTEN DATA
  const flattenedItems = useMemo(() => {
    const items = [];
    filteredDocs.forEach(doc => {
      doc.components?.forEach(comp => {
        const isMatch = skuChips.length === 0 || skuChips.some(ts => comp.sku?.toLowerCase().includes(ts));
        if (isMatch) {
          items.push({
            ...comp,
            parentSlip: doc.slipNo,
            parentId: doc.id,
            slipDate: doc.slipDate,
            fromWarehouse: doc.fromWarehouse,
            toWarehouse: doc.toWarehouse,
            requestedBy: doc.requestedBy,
            justification: doc.businessJustification,
            mocNo: doc.mocNo,
            mocDate: doc.mocDate,
            status: doc.status,
            txnType: doc.txnType,
            reasonCode: doc.reasonCode,
            totalQty: Number(comp.baseQty || 0) * (doc.multiplier || 1)
          });
        }
      });
    });
    return items;
  }, [filteredDocs, skuChips]);

  const totalItemQty = useMemo(() => flattenedItems.reduce((sum, item) => sum + item.totalQty, 0), [flattenedItems]);

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this record?")) {
      try { await deleteDoc(doc(db, "inventory_transfers", id)); } catch (e) { alert(e.message); }
    }
  };

  const getStatusBadge = (status) => {
    const styles = { display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '20px', fontSize: '10px', fontWeight: 'bold', textTransform: 'uppercase' };
    return status === 'Confirmed' 
      ? <span style={{ ...styles, backgroundColor: '#dcfce7', color: '#166534' }}>CONFIRMED</span>
      : <span style={{ ...styles, backgroundColor: '#f3f4f6', color: '#64748b' }}>DRAFT</span>;
  };

  if (loading) return <Spinner />;

  const warehouses = ["Bhiwandi", "Ahmedabad", "Delhi", "Jaipur", "Hyderabad"];
  const txnTypes = ["Transfer", "Conversion"];
  const reasonCodes = ["SAL2RNT", "RNT2DMG", "IBTRSNFR"];

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>Inventory Transfers</h1>
          <p style={{ fontSize: '14px', color: '#64748b', margin: '5px 0 0 0' }}>{viewMode === 'slips' ? 'Document list view' : 'Flattened items and movement quantities'}</p>
        </div>
        <div style={{ display: 'flex', gap: '15px' }}>
          <div style={toggleContainer}>
            <button onClick={() => setViewMode('slips')} style={viewMode === 'slips' ? activeToggle : inactiveToggle}><List size={16} /> Slips</button>
            <button onClick={() => setViewMode('items')} style={viewMode === 'items' ? activeToggle : inactiveToggle}><Table size={16} /> Item Report</button>
          </div>
          <button onClick={() => navigate('/ops/inventory-transfer/new')} style={primaryBtnStyle}><Plus size={18} /> New Transfer</button>
        </div>
      </div>

      {/* FILTER PANEL */}
      <div style={filterPanelStyle}>
        <div style={{ display: 'flex', gap: '12px', width: '100%', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          
          {/* SEARCHABLE CHIP INPUT */}
          <div style={{ flex: 1, minWidth: '300px', position: 'relative' }} ref={searchRef}>
            <label style={filterLabel}>Filter SKU(s) / Part Name</label>
            <div style={chipInputContainer}>
              {skuChips.map(chip => (
                <span key={chip} style={skuChip}>
                  {chip}
                  <X size={12} style={{ cursor: 'pointer' }} onClick={() => removeSkuChip(chip)} />
                </span>
              ))}
              <input 
                type="text" 
                placeholder={skuChips.length === 0 ? "Search SKU or Name..." : ""} 
                value={skuInput} 
                onChange={(e) => { setSkuInput(e.target.value); setShowSuggestions(true); }} 
                onKeyDown={handleSkuKeyDown}
                style={skuBareInput}
              />
            </div>
            {showSuggestions && searchedProducts.length > 0 && (
              <div style={dropdownStyle}>
                {searchedProducts.map(p => (
                  <div key={p.id} onClick={() => addSkuChip(p.sku)} style={dropdownItemStyle} className="hover-highlight">
                    <div style={{ fontWeight: 'bold', fontSize: '13px' }}>{p.sku}</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>{p.name}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ width: '110px' }}>
            <label style={filterLabel}>From WH</label>
            <select value={fromWh} onChange={(e) => setFromWh(e.target.value)} style={filterSelectStyle}>
              <option value="All">All</option>
              {warehouses.map(w => <option key={w} value={w}>{w}</option>)}
            </select>
          </div>
          <div style={{ width: '110px' }}>
            <label style={filterLabel}>To WH</label>
            <select value={toWh} onChange={(e) => setToWh(e.target.value)} style={filterSelectStyle}>
              <option value="All">All</option>
              {warehouses.map(w => <option key={w} value={w}>{w}</option>)}
            </select>
          </div>

          <div style={{ width: '120px' }}>
            <label style={filterLabel}>Txn Type</label>
            <select value={txnTypeFilter} onChange={(e) => setTxnTypeFilter(e.target.value)} style={filterSelectStyle}>
              <option value="All">All Types</option>
              {txnTypes.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          <div style={{ width: '120px' }}>
            <label style={filterLabel}>Reason</label>
            <select value={reasonCodeFilter} onChange={(e) => setReasonCodeFilter(e.target.value)} style={filterSelectStyle}>
              <option value="All">All Reasons</option>
              {reasonCodes.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>

          <div style={{ width: '130px' }}>
            <label style={filterLabel}>Start Date</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={dateInputStyle} />
          </div>
          <div style={{ width: '130px' }}>
            <label style={filterLabel}>End Date</label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={dateInputStyle} />
          </div>
        </div>
      </div>

      {/* TABLE */}
      <div style={tableContainerStyle}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e2e8f0' }}>
              {viewMode === 'slips' ? (
                <>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Slip No</th>
                  <th style={thStyle}>Movement</th>
                  <th style={thStyle}>Type / Reason</th>
                  <th style={thStyle}>Requested By</th>
                  <th style={thStyle}>MOC Ref</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Justification</th>
                </>
              ) : (
                <>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>SKU</th>
                  <th style={thStyle}>Qty</th>
                  <th style={thStyle}>Movement</th>
                  <th style={thStyle}>Requested By</th>
                  <th style={thStyle}>MOC Ref</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Justification</th>
                  <th style={thStyle}>Ref Slip</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {viewMode === 'slips' ? (
              filteredDocs.length === 0 ? (
                <tr><td colSpan="8" style={emptyTd}>No records found.</td></tr>
              ) : (
                filteredDocs.map((t) => (
                  <tr key={t.id} style={trStyle} className="hover-highlight" onClick={() => navigate(`/ops/inventory-transfer/${t.id}`)}>
                    <td style={tdStyle}>{t.slipDate}</td>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 'bold' }}>{t.slipNo}</div>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <span style={whBadge}>{t.fromWarehouse}</span>
                        <ChevronRight size={12} color="#94a3b8" />
                        <span style={{ ...whBadge, backgroundColor: '#eff6ff', color: '#2563eb' }}>{t.toWarehouse}</span>
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ fontSize: '13px', fontWeight: '500' }}>{t.txnType}</div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>{t.reasonCode}</div>
                    </td>
                    <td style={tdStyle}>{t.requestedBy}</td>
                    <td style={tdStyle}>
                       <div style={{ fontSize: '12px' }}>{t.mocNo || '---'}</div>
                       <div style={{ fontSize: '11px', color: '#94a3b8' }}>{t.mocDate}</div>
                    </td>
                    <td style={tdStyle}>{getStatusBadge(t.status)}</td>
                    <td style={{ ...tdStyle, maxWidth: '200px' }}>
                      <div style={{ fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.businessJustification || '---'}</div>
                    </td>
                  </tr>
                ))
              )
            ) : (
              flattenedItems.length === 0 ? (
                <tr><td colSpan="9" style={emptyTd}>No items found.</td></tr>
              ) : (
                flattenedItems.map((item, idx) => (
                  <tr key={idx} style={trStyle} className="hover-highlight" onClick={() => navigate(`/ops/inventory-transfer/${item.parentId}`)}>
                    <td style={tdStyle}>{item.slipDate}</td>
                    <td style={tdStyle}><strong>{item.sku}</strong></td>
                    <td style={{ ...tdStyle, fontWeight: 'bold', color: '#2563eb' }}>{item.totalQty}</td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <span style={whBadge}>{item.fromWarehouse}</span>
                        <ChevronRight size={12} color="#94a3b8" />
                        <span style={{ ...whBadge, backgroundColor: '#eff6ff', color: '#2563eb' }}>{item.toWarehouse}</span>
                      </div>
                    </td>
                    <td style={tdStyle}>{item.requestedBy}</td>
                    <td style={tdStyle}>
                       <div style={{ fontSize: '12px' }}>{item.mocNo || '---'}</div>
                       {item.mocDate && <div style={{ fontSize: '11px', color: '#94a3b8' }}>{item.mocDate}</div>}
                    </td>
                    <td style={tdStyle}>{getStatusBadge(item.status)}</td>
                    <td style={{ ...tdStyle, maxWidth: '150px' }}>
                       <div style={{ fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.justification || '---'}</div>
                    </td>
                    <td style={tdStyle}><div style={{ fontSize: '11px', color: '#64748b' }}>{item.parentSlip}</div></td>
                  </tr>
                ))
              )
            )}
          </tbody>
          {viewMode === 'items' && flattenedItems.length > 0 && (
            <tfoot style={{ position: 'sticky', bottom: 0, backgroundColor: '#f8fafc', borderTop: '2px solid #e2e8f0', zIndex: 1 }}>
              <tr style={{ fontWeight: '800' }}>
                <td style={tdStyle}>Total Summary</td>
                <td style={tdStyle}>-</td>
                <td style={{ ...tdStyle, color: '#2563eb', fontSize: '16px' }}>{totalItemQty}</td>
                <td colSpan="6" style={tdStyle}>Across {flattenedItems.length} movement records</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

// --- STYLES ---
const toggleContainer = { display: 'flex', backgroundColor: '#f1f5f9', padding: '4px', borderRadius: '8px', border: '1px solid #e2e8f0' };
const inactiveToggle = { display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', border: 'none', background: 'none', fontSize: '13px', color: '#64748b', cursor: 'pointer', borderRadius: '6px' };
const activeToggle = { ...inactiveToggle, backgroundColor: 'white', color: '#2563eb', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', fontWeight: 'bold' };
const filterPanelStyle = { backgroundColor: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '20px' };
const filterLabel = { fontSize: '10px', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '4px', display: 'block' };
const filterInputStyle = { padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', outline: 'none', fontSize: '13px', width: '100%', boxSizing: 'border-box' };
const filterSelectStyle = { ...filterInputStyle, backgroundColor: 'white', cursor: 'pointer' };
const dateInputStyle = { ...filterInputStyle, padding: '6px' };
const tableContainerStyle = { backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' };
const thStyle = { padding: '12px 20px', textAlign: 'left', fontSize: '11px', fontWeight: 'bold', color: '#64748b', textTransform: 'uppercase' };
const tdStyle = { padding: '12px 20px', fontSize: '13px', color: '#374151', borderBottom: '1px solid #f1f5f9' };
const trStyle = { cursor: 'pointer' };
const whBadge = { backgroundColor: '#f1f5f9', padding: '3px 6px', borderRadius: '4px', fontSize: '11px', color: '#475569' };
const primaryBtnStyle = { display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', borderRadius: '8px', border: 'none', backgroundColor: '#2563eb', color: 'white', fontWeight: '600', cursor: 'pointer' };
const emptyTd = { padding: '40px', textAlign: 'center', color: '#94a3b8' };

const chipInputContainer = { display: 'flex', flexWrap: 'wrap', gap: '5px', padding: '6px 10px', borderRadius: '6px', border: '1px solid #e2e8f0', minHeight: '38px', alignItems: 'center', boxSizing: 'border-box', backgroundColor: 'white' };
const skuChip = { display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: '#eff6ff', color: '#2563eb', padding: '2px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold', border: '1px solid #bfdbfe' };
const skuBareInput = { border: 'none', outline: 'none', fontSize: '13px', flex: 1, minWidth: '100px' };
const dropdownStyle = { position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', zIndex: 100, boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', maxHeight: '250px', overflowY: 'auto', marginTop: '4px' };
const dropdownItemStyle = { padding: '10px 15px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9' };

export default InventoryTransferList;